import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  Plus, 
  AlertCircle, 
  CheckCircle2,
  Clock,
  Upload,
  DollarSign,
  Info,
  TrendingUp,
  XCircle
} from 'lucide-react';
import { calculateFlexBalance } from '@/components/utils/taxCalculations';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { isFeatureEnabled } from '@/components/utils/featureFlags';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function Claims() {
  const [employee, setEmployee] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [flexInfo, setFlexInfo] = useState(null);
  const [flexAllocation, setFlexAllocation] = useState(0);
  const [totalPremiums, setTotalPremiums] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    claim_type: 'medical',
    claim_amount: '',
    claim_date: new Date().toISOString().split('T')[0],
    description: '',
    receipt_url: ''
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    checkFeature();
    loadData();
  }, []);

  const checkFeature = async () => {
    const enabled = await isFeatureEnabled('claims_enabled');
    setIsEnabled(enabled);
  };

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        const [enrollmentsData, claimsData] = await Promise.all([
          api.entities.Enrollment.filter({ 
            employee_id: emp.id,
            status: 'active'
          }),
          api.entities.Claim.filter({ employee_id: emp.id })
        ]);
        
        setEnrollments(enrollmentsData);
        setClaims(claimsData);
        
        // Calculate flex balance
        const totalPremiums = enrollmentsData.reduce((sum, e) => sum + (e.monthly_premium || 0), 0);
        const flexCalc = calculateFlexBalance(emp.flex_allocation || 0, totalPremiums);
        
        setFlexAllocation(emp.flex_allocation || 0);
        setTotalPremiums(totalPremiums);
        setFlexInfo(flexCalc);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelClaim = async (claimId) => {
    try {
      await api.entities.Claim.update(claimId, {
        status: 'cancelled'
      });
      
      toast.success('Claim cancelled successfully');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to cancel claim');
    }
  };

  const confirmCancelClaim = (claim) => {
    openConfirm({
      title: 'Cancel this claim request?',
      description: 'The request will be stopped and no reimbursement will be processed. You can submit a new claim with updated information later.',
      confirmLabel: 'Cancel claim request',
      isDestructive: true,
      onConfirm: () => cancelClaim(claim.id)
    });
  };

  const handleSubmitClaim = async () => {
    // Validation
    if (!formData.claim_amount || Number(formData.claim_amount) <= 0) {
      toast.error('Please enter a valid claim amount');
      return;
    }
    
    if (!formData.description?.trim()) {
      toast.error('Please provide a description of the expense');
      return;
    }

    if (!selectedFile && !formData.receipt_url) {
      toast.error('Please upload a receipt or proof of expense');
      return;
    }
    
    const claimAmount = Number(formData.claim_amount);
    
    // Validate against 86% rule
    if (flexInfo && claimAmount > flexInfo.maxClaimable) {
      toast.error(`Claim amount exceeds 86% limit. Maximum: $${flexInfo.maxClaimable.toFixed(2)}`);
      return;
    }

    // Check if they have enough balance
    if (flexInfo && claimAmount > flexInfo.flexBalance) {
      toast.error(`Claim amount exceeds your premium balance of $${flexInfo.flexBalance.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      // Upload receipt if provided
      let receiptUrl = formData.receipt_url;
      if (selectedFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('receipt', selectedFile);
        
        const uploadResponse = await fetch('http://localhost:3001/api/uploads/receipt', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formDataUpload
        });

        if (!uploadResponse.ok) {
          throw new Error('File upload failed');
        }

        const uploadResult = await uploadResponse.json();
        receiptUrl = uploadResult.url;
        toast.success('📎 Receipt uploaded successfully');
      }

      await api.entities.Claim.create({
        employee_id: employee.id,
        ...formData,
        claim_amount: claimAmount,
        receipt_url: receiptUrl,
        status: 'pending'
      });
      
      toast.success('✅ Claim submitted successfully! HR will review within 2-3 business days.');
      setShowModal(false);
      setSelectedFile(null);
      setFormData({
        claim_type: 'medical',
        claim_amount: '',
        claim_date: new Date().toISOString().split('T')[0],
        description: '',
        receipt_url: ''
      });
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit claim. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      denied: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      paid: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
      cancelled: { color: 'bg-slate-100 text-slate-600', icon: XCircle }
    };
    
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <Badge className={style.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold">Claims Feature Disabled</h2>
            <p className="text-muted-foreground">
              The claims management feature is currently disabled by the system administrator.
              Please contact your HR department for more information.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Premium Spending Reimbursements</h1>
          <p className="text-slate-500 mt-1">Get reimbursed for out-of-pocket medical expenses</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="self-start sm:self-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Request Reimbursement
        </Button>
      </div>

      {flexInfo && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Main Balance Card */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                      <h3 className="text-lg font-semibold text-slate-900">Actual Remaining to Claim</h3>
                    </div>
                    <div className="flex items-baseline gap-4">
                      <p className="text-5xl font-bold text-emerald-600">
                        ${Math.max(0, flexInfo.maxClaimable - claims.filter(c => c.status === 'pending' || c.status === 'approved' || c.status === 'paid').reduce((sum, c) => sum + Number(c.claim_amount), 0)).toFixed(2)}
                      </p>
                      <div className="text-slate-600">
                        <p className="text-sm">of ${flexInfo.maxClaimable.toFixed(2)} max</p>
                        <p className="text-xs text-slate-500">(86% rule applied)</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">After pending & paid claims deducted</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
                  >
                    {showBreakdown ? 'Hide' : 'Show'} Math
                    <TrendingUp className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {showBreakdown && (
                  <div className="bg-white rounded-lg p-4 space-y-2 border border-emerald-200 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Premium Allocation</span>
                      <span className="font-semibold text-slate-900">${Number(flexAllocation).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-red-600">
                      <span>− Benefit Premiums</span>
                      <span className="font-semibold">−${Number(totalPremiums).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                      <span className="font-medium text-slate-700">= Remaining Balance</span>
                      <span className="font-bold text-slate-900">${Number(flexInfo.flexBalance).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-emerald-700">
                      <span>× 86% (IRS Rule)</span>
                      <span className="font-semibold">× 0.86</span>
                    </div>
                    <div className="border-t-2 border-emerald-300 pt-2 flex justify-between items-center">
                      <span className="font-bold text-emerald-800">= Available</span>
                      <span className="font-bold text-xl text-emerald-600">${Number(flexInfo.maxClaimable).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-slate-500 mb-1">Paid YTD</p>
                    <p className="text-lg font-bold text-blue-600">
                      ${claims.filter(c => c.status === 'paid').reduce((sum, c) => sum + Number(c.claim_amount), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-slate-500 mb-1">Pending</p>
                    <p className="text-lg font-bold text-amber-600">
                      ${claims.filter(c => c.status === 'pending' || c.status === 'approved').reduce((sum, c) => sum + Number(c.claim_amount), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Info Sidebar */}
          <div className="space-y-4">
            <Alert className="bg-amber-50 border-amber-300">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-900">
                <strong className="block mb-1">Not for Insurance Claims</strong>
                Insurance claims go to your carrier. This is for <strong>reimbursing YOU</strong> for out-of-pocket costs.
              </AlertDescription>
            </Alert>

            <Alert className="bg-blue-50 border-blue-200">
              <Info className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-900">
                <strong className="block mb-1">What Qualifies:</strong>
                Co-pays, deductibles, prescriptions, dental/vision out-of-pocket costs, non-covered services, medical equipment.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Claim History</CardTitle>
        </CardHeader>
        <CardContent>
          {claims.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No claims submitted yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {claims
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((claim) => (
                <div 
                  key={claim.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-medium text-slate-900">{claim.claim_type}</p>
                      {getStatusBadge(claim.status)}
                    </div>
                    <p className="text-sm text-slate-600">{claim.description}</p>
                    {claim.receipt_url && (
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                          📎 Receipt Attached
                        </Badge>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => {
                            // Open receipt in new tab
                            const url = claim.receipt_url.startsWith('http') 
                              ? claim.receipt_url 
                              : `http://localhost:3001${claim.receipt_url}`;
                            window.open(url, '_blank');
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 h-auto p-0"
                        >
                          View Receipt
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {claim.created_at && `Submitted: ${format(new Date(claim.created_at), 'MMM d, yyyy')}`}
                      {claim.created_at && `Submitted: ${format(new Date(claim.created_at), 'MMM d, yyyy')}`}
                      {claim.claim_date && ` • Expense: ${format(new Date(claim.claim_date), 'MMM d, yyyy')}`}
                    </p>
                    {claim.approved_date && new Date(claim.approved_date).getTime() > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Approved: {format(new Date(claim.approved_date), 'MMM d, yyyy')}
                      </p>
                    )}
                    {claim.paid_date && new Date(claim.paid_date).getTime() > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        ✓ Paid: {format(new Date(claim.paid_date), 'MMM d, yyyy')}
                      </p>
                    )}
                    {claim.denial_reason && (
                      <div className={`mt-2 p-2 rounded-lg ${
                        claim.denial_reason.startsWith('More info needed:') || claim.denial_reason.startsWith('Approval revoked:')
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <p className={`text-sm ${
                          claim.denial_reason.startsWith('More info needed:') || claim.denial_reason.startsWith('Approval revoked:')
                            ? 'text-blue-700'
                            : 'text-red-600'
                        }`}>
                          {claim.denial_reason.startsWith('More info needed:') ? (
                            <>
                              <strong>ℹ️ Action Required:</strong> {claim.denial_reason.replace('More info needed:', '')}
                            </>
                          ) : claim.denial_reason.startsWith('Approval revoked:') ? (
                            <>
                              <strong>⚠️ Returned for Review:</strong> {claim.denial_reason.replace('Approval revoked:', '')}
                            </>
                          ) : (
                            <>
                              <strong>Denied:</strong> {claim.denial_reason}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {/* Audit History Timeline */}
                    {claim.audit_history && claim.audit_history !== 'null' && JSON.parse(claim.audit_history).length > 0 && (
                      <details className="mt-3 group">
                        <summary className="cursor-pointer text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
                          <span>📋 View Activity History ({JSON.parse(claim.audit_history).length})</span>
                          <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-2">
                          {JSON.parse(claim.audit_history)
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((entry, idx) => (
                            <div key={idx} className="text-xs">
                              <p className="text-slate-700">
                                <strong className={`${
                                  entry.action === 'approved' ? 'text-green-600' :
                                  entry.action === 'denied' ? 'text-red-600' :
                                  entry.action === 'paid' ? 'text-emerald-600' :
                                  entry.action === 'unapproved' ? 'text-amber-600' :
                                  'text-blue-600'
                                }`}>
                                  {entry.action === 'approved' ? '✓ Approved' :
                                   entry.action === 'denied' ? '✗ Denied' :
                                   entry.action === 'paid' ? '💳 Paid' :
                                   entry.action === 'unapproved' ? '⚠️ Unapproved' :
                                   'ℹ️ Info Requested'}
                                </strong> - {entry.note}
                              </p>
                              <p className="text-slate-500">
                                {format(new Date(entry.date), 'MMM d, h:mm a')} by {entry.by}
                              </p>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-900">
                        ${Number(claim.claim_amount).toFixed(2)}
                      </p>
                    </div>
                    {claim.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmCancelClaim(claim)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Reimbursement for Out-of-Pocket Expense</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-900">
                <strong>Submit expenses YOU paid:</strong> This is for out-of-pocket costs like co-pays, deductibles, 
                or non-covered expenses - NOT for filing insurance claims. You can currently claim up to: 
                <strong className="text-emerald-700"> ${flexInfo ? Math.max(0, flexInfo.maxClaimable - claims.filter(c => c.status === 'pending' || c.status === 'approved' || c.status === 'paid').reduce((sum, c) => sum + Number(c.claim_amount), 0)).toFixed(2) : '0.00'}</strong>
                {flexInfo && claims.filter(c => c.status === 'pending' || c.status === 'approved').length > 0 && (
                  <span className="text-amber-700"> (${claims.filter(c => c.status === 'pending' || c.status === 'approved').reduce((sum, c) => sum + Number(c.claim_amount), 0).toFixed(2)} pending)</span>
                )}. 
                Keep receipts for verification.
              </AlertDescription>
            </Alert>

            <div>
              <Label>Expense Category <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.claim_type} 
                onValueChange={(value) => setFormData({...formData, claim_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medical">💊 Medical (co-pays, deductibles, Rx)</SelectItem>
                  <SelectItem value="dental">🦷 Dental (out-of-pocket costs)</SelectItem>
                  <SelectItem value="vision">👓 Vision (glasses, contacts, exams)</SelectItem>
                  <SelectItem value="wellness">🏃 Wellness Program</SelectItem>
                  <SelectItem value="other">📋 Other Qualified Expense</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Select the type of out-of-pocket expense you paid
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Claim Amount <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.claim_amount}
                    onChange={(e) => setFormData({...formData, claim_amount: e.target.value})}
                    placeholder="0.00"
                    className="pl-9"
                  />
                </div>
                {flexInfo && (
                  <p className="text-xs text-slate-500 mt-1">
                    Available to claim: ${Math.max(0, flexInfo.maxClaimable - claims.filter(c => c.status === 'pending' || c.status === 'approved' || c.status === 'paid').reduce((sum, c) => sum + Number(c.claim_amount), 0)).toFixed(2)} 
                    (86% of ${Number(flexInfo.flexBalance).toFixed(2)})
                  </p>
                )}
              </div>
              
              <div>
                <Label>Expense Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.claim_date}
                  onChange={(e) => setFormData({...formData, claim_date: e.target.value})}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div>
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Example: Co-pay for specialist visit ($35), Deductible for ER visit ($250), Dental crown out-of-pocket after insurance ($400), Non-covered prescription ($85)"
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1">
                Describe what you paid for and the amount you paid out-of-pocket
              </p>
            </div>

            <div>
              <Label>Receipt / Proof of Payment <span className="text-red-500">*</span></Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                  id="receipt-upload"
                />
                <label htmlFor="receipt-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  {selectedFile ? (
                    <p className="text-sm font-medium text-emerald-600">✓ {selectedFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-700">Click to upload receipt</p>
                      <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Upload a receipt, invoice, or EOB showing the expense and amount you paid
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitClaim}
                disabled={submitting}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Submit Claim
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}